import React, { useState, useMemo } from 'react';
import { 
  Hotel, Users, Search, RefreshCw, Loader2, AlertTriangle,
  ChevronDown, ChevronUp, Settings, Eye, Edit2
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  deposit_paid: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  fully_paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const statusLabels = {
  pending: 'Pending',
  deposit_paid: 'Deposit Paid',
  confirmed: 'Confirmed',
  fully_paid: 'Fully Paid',
  cancelled: 'Cancelled',
};

// Room type card with occupancy indicator
const RoomTypeCard = ({ name, pricePerPerson, capacity, booked, available }) => {
  const occupancyPercent = capacity > 0 ? Math.round((booked / capacity) * 100) : 0;
  const isFull = available === 0;
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-slate-900 capitalize">{name}</h4>
        <Badge variant="outline">{formatCurrency(pricePerPerson)}/pp</Badge>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{booked} booked</span>
          <span className={cn(
            "font-medium",
            isFull ? "text-slate-500" : available <= 2 ? "text-orange-600" : "text-emerald-600"
          )}>
            {isFull ? "· Full" : `· ${available} available`}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all",
              isFull ? "bg-slate-400" : occupancyPercent >= 80 ? "bg-orange-500" : "bg-violet-500"
            )}
            style={{ width: `${occupancyPercent}%` }}
          />
        </div>
        <div className="text-xs text-slate-400 text-right">{occupancyPercent}% occupied</div>
      </div>
    </div>
  );
};

// Get room info from booking (handles array format)
const getBookingRooms = (booking) => {
  if (!booking.rooms || !Array.isArray(booking.rooms)) return [];
  return booking.rooms.filter(r => r.quantity > 0);
};

const getRoomSummary = (booking) => {
  const rooms = getBookingRooms(booking);
  if (rooms.length === 0) return { text: 'No room', hasRoom: false };
  if (rooms.length === 1) {
    return { 
      text: `${rooms[0].quantity}x ${rooms[0].room_type}`, 
      hasRoom: true,
      value: rooms[0].total_price || (rooms[0].price_per_person * rooms[0].quantity * (rooms[0].occupancy || 1))
    };
  }
  const totalValue = rooms.reduce((sum, r) => sum + (r.total_price || 0), 0);
  return { text: `${rooms.length} room types`, hasRoom: true, value: totalValue };
};

export default function WeekenderRooms({ 
  event, 
  summaryData,
  bookings, 
  loading, 
  authToken,
  onRefresh,
  onBookingSelect
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [roomFilter, setRoomFilter] = useState('all');
  const [sortField, setSortField] = useState('room_type');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showRecalculateDialog, setShowRecalculateDialog] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  
  // Set Room Counts (manual override) state
  const [showSetRoomCountsDialog, setShowSetRoomCountsDialog] = useState(false);
  const [roomCountsForm, setRoomCountsForm] = useState({});
  const [savingRoomCounts, setSavingRoomCounts] = useState(false);

  // Room summary from event summary API
  const rooms = summaryData?.rooms || {};
  const totalCapacity = Object.values(rooms).reduce((sum, r) => sum + (r.capacity || 0), 0);
  const totalBooked = Object.values(rooms).reduce((sum, r) => sum + (r.booked || 0), 0);
  const totalAvailable = totalCapacity - totalBooked;

  // Filter and sort bookings for allocation view
  const filteredBookings = useMemo(() => {
    let filtered = [...(bookings || [])];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.customer_name?.toLowerCase().includes(query) ||
        b.customer_email?.toLowerCase().includes(query) ||
        b.booking_id?.toLowerCase().includes(query)
      );
    }
    
    // Room type filter
    if (roomFilter === 'no_room') {
      filtered = filtered.filter(b => getBookingRooms(b).length === 0);
    } else if (roomFilter !== 'all') {
      filtered = filtered.filter(b => 
        getBookingRooms(b).some(r => r.room_type?.toLowerCase() === roomFilter.toLowerCase())
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'customer':
          aVal = a.customer_name || '';
          bVal = b.customer_name || '';
          break;
        case 'room_type':
          const aRooms = getBookingRooms(a);
          const bRooms = getBookingRooms(b);
          aVal = aRooms.length > 0 ? aRooms[0].room_type : 'zzz';
          bVal = bRooms.length > 0 ? bRooms[0].room_type : 'zzz';
          break;
        case 'guests':
          aVal = a.number_of_guests || 0;
          bVal = b.number_of_guests || 0;
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = a.booking_id;
          bVal = b.booking_id;
      }
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [bookings, searchQuery, roomFilter, sortField, sortDirection]);

  // Get unique room types for filter
  const roomTypes = useMemo(() => {
    return Object.keys(rooms);
  }, [rooms]);

  // Count bookings without rooms
  const noRoomCount = useMemo(() => {
    return (bookings || []).filter(b => getBookingRooms(b).length === 0).length;
  }, [bookings]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-3 h-3 ml-1" /> : 
      <ChevronDown className="w-3 h-3 ml-1" />;
  };

  // Recalculate availability handler
  const handleRecalculateAvailability = async () => {
    setRecalculating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}/recalculate-availability`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Room availability recalculated');
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to recalculate availability');
      }
    } catch (error) {
      toast.error('Failed to recalculate availability');
    } finally {
      setRecalculating(false);
      setShowRecalculateDialog(false);
    }
  };

  // Open Set Room Counts dialog with current values
  const openSetRoomCountsDialog = () => {
    const initialCounts = {};
    Object.entries(rooms).forEach(([type, room]) => {
      initialCounts[type] = {
        available: room.capacity || 0,
        booked: room.booked || 0
      };
    });
    setRoomCountsForm(initialCounts);
    setShowSetRoomCountsDialog(true);
  };

  // Handle saving manual room count overrides
  // Use case: Rooms allocated by hotel, organiser accommodation, offline reservations
  const handleSaveRoomCounts = async () => {
    setSavingRoomCounts(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}/set-room-availability`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ rooms: roomCountsForm })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(`Updated ${result.updates?.length || 0} room types`);
        setShowSetRoomCountsDialog(false);
        onRefresh?.();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to update room counts');
      }
    } catch (error) {
      toast.error('Error updating room counts');
    } finally {
      setSavingRoomCounts(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Rooms</h2>
          <p className="text-sm text-slate-500">Room inventory and booking allocations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="text-slate-500 hover:text-slate-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Refresh
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={openSetRoomCountsDialog}
            disabled={loading || Object.keys(rooms).length === 0}
            className="text-slate-500 hover:text-slate-700"
            data-testid="set-room-counts-btn"
            title="Manually adjust room counts for offline/external allocations"
          >
            <Edit2 className="w-4 h-4 mr-1.5" />
            Set Manually
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRecalculateDialog(true)}
            className="text-slate-500 hover:text-slate-700"
            data-testid="recalculate-availability-btn"
          >
            <Settings className="w-4 h-4 mr-1.5" />
            Recalculate
          </Button>
        </div>
      </div>

      {/* Room Summary */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Hotel className="w-4 h-4 text-violet-600" />
              Room Inventory
            </CardTitle>
            <Badge variant="outline" className="text-sm">
              {totalBooked}/{totalCapacity} rooms · {totalAvailable} available
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : Object.keys(rooms).length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(rooms).map(([type, data]) => (
                <RoomTypeCard
                  key={type}
                  name={data.name || type}
                  pricePerPerson={data.price_per_person || 0}
                  capacity={data.capacity || 0}
                  booked={data.booked || 0}
                  available={data.available || 0}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">
              No room types configured
            </p>
          )}
        </CardContent>
      </Card>

      {/* Room Allocations */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-600" />
              Room Allocations
            </CardTitle>
            {noRoomCount > 0 && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {noRoomCount} without room
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, booking ref..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={roomFilter} onValueChange={setRoomFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by room" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rooms</SelectItem>
                <SelectItem value="no_room">No Room Assigned</SelectItem>
                {roomTypes.map(type => (
                  <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Count */}
          <div className="text-sm text-slate-500 mb-3">
            {filteredBookings.length} of {bookings?.length || 0} bookings
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th 
                    className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('booking_id')}
                  >
                    <span className="flex items-center">BOOKING REF <SortIcon field="booking_id" /></span>
                  </th>
                  <th 
                    className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('customer')}
                  >
                    <span className="flex items-center">LEAD BOOKER <SortIcon field="customer" /></span>
                  </th>
                  <th 
                    className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('guests')}
                  >
                    <span className="flex items-center">GUESTS <SortIcon field="guests" /></span>
                  </th>
                  <th 
                    className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('room_type')}
                  >
                    <span className="flex items-center">ROOM <SortIcon field="room_type" /></span>
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-600">VALUE</th>
                  <th 
                    className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('status')}
                  >
                    <span className="flex items-center">STATUS <SortIcon field="status" /></span>
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-600">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      {searchQuery || roomFilter !== 'all' 
                        ? 'No bookings match your filters' 
                        : 'No bookings yet'}
                    </td>
                  </tr>
                ) : filteredBookings.map((booking) => {
                  const roomInfo = getRoomSummary(booking);
                  return (
                    <tr 
                      key={booking.booking_id} 
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => onBookingSelect?.(booking)}
                    >
                      <td className="py-3 px-3">
                        <span className="font-mono text-xs text-slate-600">
                          {booking.booking_id?.slice(0, 15)}...
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="font-medium text-slate-900">{booking.customer_name}</div>
                        <div className="text-xs text-slate-500">{booking.customer_email}</div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          {booking.number_of_guests || 1}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className={cn(
                          "font-medium capitalize",
                          roomInfo.hasRoom ? "text-slate-900" : "text-orange-600"
                        )}>
                          {roomInfo.text}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        {roomInfo.hasRoom && roomInfo.value > 0 ? (
                          <span className="text-slate-700">{formatCurrency(roomInfo.value)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <Badge className={cn("text-xs", statusColors[booking.status])}>
                          {statusLabels[booking.status] || booking.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onBookingSelect?.(booking);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filteredBookings.length === 0 ? (
              <p className="text-center py-8 text-slate-500">
                {searchQuery || roomFilter !== 'all' 
                  ? 'No bookings match your filters' 
                  : 'No bookings yet'}
              </p>
            ) : filteredBookings.map((booking) => {
              const roomInfo = getRoomSummary(booking);
              return (
                <div 
                  key={booking.booking_id}
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onBookingSelect?.(booking)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-slate-900">{booking.customer_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{booking.booking_id?.slice(0, 15)}...</p>
                    </div>
                    <Badge className={cn("text-xs", statusColors[booking.status])}>
                      {statusLabels[booking.status] || booking.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-slate-600">
                      <Users className="w-3.5 h-3.5" /> {booking.number_of_guests || 1} guests
                    </span>
                    <span className={cn(
                      "font-medium capitalize",
                      roomInfo.hasRoom ? "text-slate-900" : "text-orange-600"
                    )}>
                      {roomInfo.text}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recalculate Confirmation Dialog */}
      <AlertDialog open={showRecalculateDialog} onOpenChange={setShowRecalculateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate Room Availability</AlertDialogTitle>
            <AlertDialogDescription>
              This will recalculate room availability counts based on actual bookings. 
              This is a maintenance action and should only be used if availability figures 
              appear incorrect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRecalculateAvailability}
              disabled={recalculating}
            >
              {recalculating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recalculating...
                </>
              ) : (
                'Recalculate'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set Room Counts Dialog (Manual Override) */}
      {/* Use case: Rooms allocated by hotel, organiser accommodation, offline/external reservations */}
      <Dialog open={showSetRoomCountsDialog} onOpenChange={setShowSetRoomCountsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Set Room Availability</DialogTitle>
            <DialogDescription>
              Manually adjust room counts for offline allocations (hotel direct bookings, 
              organiser/artist accommodation, external reservations).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {Object.entries(roomCountsForm).map(([roomType, counts]) => (
              <div key={roomType} className="border rounded-lg p-3">
                <h4 className="font-medium text-slate-900 mb-2">{roomType}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-slate-600">Total Capacity</Label>
                    <Input
                      type="number"
                      min="0"
                      value={counts.available}
                      onChange={(e) => setRoomCountsForm(prev => ({
                        ...prev,
                        [roomType]: { ...prev[roomType], available: parseInt(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Booked Count</Label>
                    <Input
                      type="number"
                      min="0"
                      value={counts.booked}
                      onChange={(e) => setRoomCountsForm(prev => ({
                        ...prev,
                        [roomType]: { ...prev[roomType], booked: parseInt(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Remaining: {(counts.available || 0) - (counts.booked || 0)}
                </p>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetRoomCountsDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRoomCounts}
              disabled={savingRoomCounts}
              className="bg-violet-600 hover:bg-violet-500"
            >
              {savingRoomCounts ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
